<template>
  <div v-if="!!store.plan" class="asset-management-plan">
    <v-navigation-drawer>
      <div class="draw-actions">
        <v-btn
          icon="mdi-arrow-left"
          density="compact"
          variant="plain"
          :disabled="!!loading"
          @click="$router.push('/asset-management-plans')"
        />
        <v-spacer />
        <v-btn
          v-if="!readonly"
          color="secondary"
          flat
          :loading="!!loading"
          @click="save()"
        >
          <v-icon class="mr-1">mdi-content-save</v-icon>Save
        </v-btn>
        <v-btn
          v-else-if="
            store.plan.id ===
              store.plan.asset_management_plan_master.metrics
                .latest_revision_id &&
            (store.plan.status === 'Published' ||
              store.plan.status === 'Archived')
          "
          color="secondary"
          flat
          :loading="!!loading"
          @click="openCreateRevisionDialog"
        >
          <v-icon class="mr-1">mdi-plus</v-icon>Revision
        </v-btn>
      </div>
      <v-divider></v-divider>
      <v-list-item
        title="Asset Management Plan"
        :subtitle="
          (store.plan.asset_management_plan_master.name ?? '') +
          '-' +
          (store.plan.revision ?? '')
        "
      >
      </v-list-item>
      <v-divider></v-divider>
      <v-list v-model="tabKey">
        <v-list-item
          v-for="section in sections"
          :key="section.key"
          link
          :title="section.title"
          :active="section.key === tabKey"
          @click="tabKey = section.key"
        >
          <template v-if="validSections[section.key] === false" #append>
            <TooltipIcon text="Please fill out all fields." />
          </template>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>
    <v-tabs-window :model-value="tabKey" direction="vertical" class="content">
      <v-tabs-window-item value="general" key="general">
        <GeneralComponent
          class="tab-window"
          :readonly="readonly"
          @delete="deleteItem"
          @refresh="refreshPlan"
        >
          <template #revision-actions>
            <v-btn
              v-if="!readonly || publishing"
              class="ml-2"
              color="secondary"
              flat
              :disabled="!store.plan.valid || !!loading"
              :loading="publishing"
              @click="publish"
            >
              <v-icon class="mr-1" icon="mdi-send" />
              Publish
            </v-btn>
            <v-btn
              v-if="
                store.plan.status === 'Draft' ||
                store.plan.status === 'In Review'
              "
              class="ml-2"
              variant="outlined"
              :disabled="!!loading"
              :loading="archiving"
              @click="archive"
            >
              <v-icon class="mr-1" icon="mdi-archive" />
              Archive
            </v-btn>
          </template>
        </GeneralComponent>
      </v-tabs-window-item>
      <v-tabs-window-item value="introduction" key="introduction">
        <IntroductionComponent
          class="tab-window"
          :readonly="readonly"
          @delete="deleteItem"
        />
      </v-tabs-window-item>
      <v-tabs-window-item value="raci" key="raci">
        <RACIComponent
          class="tab-window"
          :readonly="readonly"
          @delete="deleteItem"
        />
      </v-tabs-window-item>
      <v-tabs-window-item
        value="scope-boundary-limits"
        key="scope-boundary-limits"
      >
        <ScopeBoundaryLimitsComponent
          class="tab-window"
          :readonly="readonly"
          @delete="deleteItem"
        />
      </v-tabs-window-item>
      <v-tabs-window-item value="asset-information" key="asset-information">
        <AssetInformationComponent
          class="tab-window"
          :readonly="readonly"
          @delete="deleteItem"
        />
      </v-tabs-window-item>
      <v-tabs-window-item
        value="asset-management-plan"
        key="asset-management-plan"
      >
        <AssetManagementPlanComponent
          class="tab-window"
          :readonly="readonly"
          @delete="deleteItem"
        />
      </v-tabs-window-item>
      <v-tabs-window-item value="service-levels-kpis" key="service-levels-kpis">
        <ServiceLevelsKPIsComponent
          class="tab-window"
          :readonly="readonly"
          @delete="deleteItem"
        />
      </v-tabs-window-item>
      <v-tabs-window-item
        value="life-cycle-cost-modelling"
        key="life-cycle-cost-modelling"
      >
        <LifeCycleCostModellingComponent
          class="tab-window"
          :readonly="readonly"
          @delete="deleteItem"
        />
      </v-tabs-window-item>
      <v-tabs-window-item value="improvement-actions" key="improvement-actions">
        <ImprovementActionsComponent
          class="tab-window"
          :readonly="readonly"
          @delete="deleteItem"
        />
      </v-tabs-window-item>
    </v-tabs-window>

    <BaseDialog
      v-model="createRevisionDialog"
      max-width="400"
      title="Create Revision"
    >
      <v-text-field
        v-model="newRevisionName"
        label="Revision Name"
        density="compact"
        variant="outlined"
        hide-details
      />
      <small class="d-block mt-2">
        This will create a copy based on the current plan and set it as the
        latest revision.
      </small>
      <template #actions>
        <v-btn
          :disabled="!newRevisionName"
          color="primary"
          variant="text"
          :loading="creatingRevision"
          @click="createRevision"
        >
          Create
        </v-btn>
      </template>
    </BaseDialog>
  </div>
  <div v-else class="loading-page">
    <v-progress-circular :size="70" :width="7" color="blue" indeterminate />
  </div>
</template>

<script setup lang="ts">
import {
  AssetManagementPlan,
  FLOCBoundary,
  IndividualAssetManagementPlan,
  RACIData,
  RACIRole,
  RACITask,
  ReliabilityMetricsLink,
  RiskRegisterLink,
  WorkManagementMetricsLink,
} from "@/classes";
import GeneralComponent from "@/components/asset-management-plan/General.vue";
import IntroductionComponent from "@/components/asset-management-plan/sections/Introduction.vue";
import RACIComponent from "@/components/asset-management-plan/sections/RACI.vue";
import ScopeBoundaryLimitsComponent from "@/components/asset-management-plan/sections/ScopeBoundaryLimits.vue";
import AssetInformationComponent from "@/components/asset-management-plan/sections/AssetInformation.vue";
import AssetManagementPlanComponent from "@/components/asset-management-plan/sections/AssetManagementPlan.vue";
import ServiceLevelsKPIsComponent from "@/components/asset-management-plan/sections/ServiceLevelsKPIs.vue";
import LifeCycleCostModellingComponent from "@/components/asset-management-plan/sections/LifeCycleCostModelling.vue";
import ImprovementActionsComponent from "@/components/asset-management-plan/sections/ImprovementActions.vue";
import { setupApiQueue } from "@/components/base/api-queue";
import { ref, onMounted, computed, watch } from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import { useNotifierStore } from "@/store/notifier";
import { useConfirmStore } from "@/store/confirm";
import type {
  Deleteable,
  DeleteableKey,
} from "@/classes/asset-management-plan";
import TooltipIcon from "@/components/base/tooltips/TooltipIcon.vue";
import BaseDialog from "@/components/base/dialogs/BaseDialog.vue";
import { useAMPStore } from "@/store/asset-management-plan";
import { useFilterStore } from "@/store/filter";
import { Section, sections } from "@/components/asset-management-plan/sections";

const { queueApiAction, loading } = setupApiQueue();

const $route = useRoute();
const $router = useRouter();
const $notifier = useNotifierStore();

const store = useAMPStore();
const filterStore = useFilterStore();
filterStore.hide();

const readonly = computed(() => {
  if (store.plan)
    return (
      store.plan.status !== "Draft" ||
      store.plan.id !==
        store.plan.asset_management_plan_master.metrics.latest_revision_id
    );
  return true;
});

const refreshPlan = () => {
  queueApiAction(async () => {
    store.plan = null;
    const queryParam = $route.query.id;
    if (Array.isArray(queryParam) || !queryParam) {
      $notifier.error("Could not find revision.");
      $router.push("/asset-management-plans");
    } else {
      store.plan = await AssetManagementPlan.get(String($route.query.id));
      store.plan.fixRelationships();
      await store.getPreventativeMaintenance();
    }
  });
};

const publishing = ref(false);
const publish = () => {
  $confirm.open({
    title: "Do you want to publish this revision?",
    content:
      "This will save the current revision and set the status to In Review. During the approval process, this revision cannot be edited.",
    color: "primary",
    onConfirm: async () => {
      publishing.value = true;
      store.plan.status = "In Review";
      await save();
      publishing.value = false;
      await refreshPlan();
    },
  });
};

const archiving = ref(false);
const archive = () => {
  $confirm.open({
    title: "Do you want to archive this revision?",
    content:
      "This will set the current revision to read-only mode and stop any ongoing approvals.",
    color: "primary",
    onConfirm: async () => {
      archiving.value = true;
      store.plan.status = "Archived";
      await save();
      archiving.value = false;
      await refreshPlan();
    },
  });
};

const deleteIds = ref<Record<DeleteableKey, string[]>>({
  individual_asset_management_plan: [],
  raci_task: [],
  raci_role: [],
  raci_data: [],
  floc_boundary: [],
  risk_register_links: [],
  work_management_metrics_links: [],
  reliability_metrics_links: [],
});

const deleteItem = (item: Deleteable) => {
  if (item instanceof IndividualAssetManagementPlan) {
    deleteIds.value.individual_asset_management_plan.push(item.id);
  }
  if (item instanceof RACIRole) {
    deleteIds.value.raci_role.push(item.id);
  }
  if (item instanceof RACITask) {
    deleteIds.value.raci_task.push(item.id);
  }
  if (item instanceof RACIData) {
    deleteIds.value.raci_data.push(item.id);
  }
  if (item instanceof FLOCBoundary) {
    deleteIds.value.floc_boundary.push(item.id);
  }
  if (item instanceof RiskRegisterLink) {
    deleteIds.value.risk_register_links.push(item.id);
  }
  if (item instanceof WorkManagementMetricsLink) {
    deleteIds.value.work_management_metrics_links.push(item.id);
  }
  if (item instanceof ReliabilityMetricsLink) {
    deleteIds.value.reliability_metrics_links.push(item.id);
  }
};

const save = async () => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
  await queueApiAction(async () => {
    await store.plan.update(deleteIds.value);
    for (const key in deleteIds.value) {
      deleteIds.value[key] = [];
    }
  });
};

const createRevisionDialog = ref(false);
const newRevisionName = ref("");
const openCreateRevisionDialog = () => {
  createRevisionDialog.value = true;
  newRevisionName.value = "";
};

const creatingRevision = ref(false);
const createRevision = async () => {
  creatingRevision.value = true;
  const copyId = await AssetManagementPlan.$axios.$post<string>(
    "/asset-management-plan/copy",
    {
      id: store.plan.id,
      revision_name: newRevisionName.value,
    },
  );
  creatingRevision.value = false;
  createRevisionDialog.value = false;
  tabKey.value = "general";
  $router.push(`/asset-management-plan?id=${copyId}`);
};

const $confirm = useConfirmStore();
onBeforeRouteLeave(async () => {
  if (readonly.value) return;
  await new Promise<void>((resolve, reject) => {
    try {
      $confirm.open({
        title: "Do you want to save your work?",
        content: `Save "${store.plan.asset_management_plan_master.name}-${store.plan.revision}" before exiting.`,
        confirmText: ["Save", "Don't Save"],
        confirmArgs: [[true], [false]],
        cancelText: "Cancel",
        color: "primary",
        onConfirm: (shouldSave) => {
          if (shouldSave) {
            save().then(() => {
              resolve();
            });
          } else {
            resolve();
          }
        },
        onCancel: () => {
          reject();
        },
      });
    } catch (err) {
      console.error(err);
      $notifier.error(err);
      reject(err);
    }
  });
});

const validSections = computed<Record<string, boolean>>(() => {
  if (store.plan) {
    return {
      introduction: store.plan.valid_section_introduction,
      raci: store.plan.valid_section_raci,
      "scope-boundary-limits": store.plan.valid_section_scope_boundary_limits,
      "asset-information": store.plan.valid_section_asset_information,
      "asset-management-plan": store.plan.valid_section_asset_management_plan,
      "service-levels-kpis": store.plan.valid_section_service_levels_kpis,
      "life-cycle-cost-modelling":
        store.plan.valid_section_life_cycle_cost_modelling,
      "improvement-actions": store.plan.valid_section_improvement_actions,
    };
  }
  return {};
});

const tab = ref<Section>(sections[0]);

const tabKey = computed({
  get: () => {
    return tab.value.key;
  },
  set: (val: string) => {
    const section = sections.find((h) => h.key === val) ?? tab.value;
    tab.value = section;
    $router.replace({
      path: "/asset-management-plan",
      query: { ...$route.query, section: section.key },
    });
    $route.query.section = tab.value.key;
  },
});

watch(
  () => $route.query.id,
  () => {
    tabKey.value = String($route.query.section);
    refreshPlan();
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.loading-page {
  width: 100%;
  height: 100%;
  align-content: center;
  text-align: center;
}

.asset-management-plan {
  width: 100%;
  height: 100%;

  .draw-actions {
    display: flex;
    flex-direction: row;
    width: 100%;
    padding-left: 16px;
    padding-right: 16px;
    padding-top: 8px;
    padding-bottom: 8px;
    align-items: center;
    height: 52px;
  }

  .content {
    width: 100%;
    height: 100%;

    .tab-window {
      height: 100%;
    }
  }
}
</style>

<style lang="scss">
.asset-management-plan {
  .content {
    .v-window-item {
      height: 100% !important;
    }
  }
}
</style>
